import React from 'react';
import { Cohort, QualityTier } from '../imageQuality/constants';
import { useIqColors } from '../imageQuality/meridianUi';

/** Scorecard-only pieces: the cohort badge on each row and the column-chooser pills. */

const COHORT_BADGE: Record<Cohort, { label: string; tier: QualityTier }> = {
  [Cohort.TOP]: { label: 'TOP', tier: 'good' },
  [Cohort.MIDDLE]: { label: 'MID', tier: 'warn' },
  [Cohort.BOTTOM]: { label: 'LOW', tier: 'bad' },
};

/** Small TOP / MID / LOW badge in the cohort's colour (success / warning / error). */
export const CohortBadge: React.FC<{ cohort: Cohort }> = ({ cohort }) => {
  const { badge } = useIqColors();
  const { label, tier } = COHORT_BADGE[cohort];
  return (
    <span
      data-testid={`iq-cohort-badge-${cohort}`}
      style={{
        background: badge[tier].fill,
        color: badge[tier].ink,
        borderRadius: 4,
        padding: '2px 7px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

/** Rounded toggle pill: "+ Label" when off, "✓ Label" when on (the mock's column chooser). */
export const TogglePill: React.FC<{
  label: string;
  selected: boolean;
  onToggle: () => void;
  testId?: string;
}> = ({ label, selected, onToggle, testId }) => {
  const { accent, pill } = useIqColors();
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onToggle}
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 16px',
        borderRadius: 999,
        border: `1px solid ${selected ? accent : pill.border}`,
        background: pill.surface,
        color: selected ? accent : pill.ink,
        fontSize: 15,
        fontWeight: selected ? 600 : 400,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden>{selected ? '✓' : '+'}</span>
      {label}
    </button>
  );
};
